import { Check, RotateCcw, Sparkles } from "lucide-react";
import type {
  LocalizedText,
  MyRoadmapData,
  ProgressStatus,
  Questionnaire,
} from "../../api/CertRoadmap/types";
import BilingualText from "./BilingualText";
import { ROADMAP_INTRO } from "./config";
import { localizedText } from "./localizedText";
import RoadmapCredentialCard from "./RoadmapCredentialCard";
import type { RoadmapLanguage } from "./types";

type RoadmapResultProps = {
  data: MyRoadmapData;
  onRetake: () => void;
  onUpdateProgress: (
    credentialId: string,
    status: ProgressStatus
  ) => void;
  updatingId?: string;
  language: RoadmapLanguage;
  questionnaire?: Questionnaire;
};

type CredentialSectionKey =
  | "next_steps"
  | "later"
  | "completed_path"
  | "explicitly_excluded";

const sections: {
  key: CredentialSectionKey;
  title: LocalizedText;
}[] = [
  { key: "next_steps", title: { en: "Next steps", ar: "الخطوات التالية" } },
  { key: "later", title: { en: "Later", ar: "لاحقًا" } },
  {
    key: "completed_path",
    title: { en: "Completed path", ar: "الشهادات المكتملة" },
  },
  {
    key: "explicitly_excluded",
    title: { en: "Not recommended for you", ar: "غير موصى بها لك" },
  },
];

const progressFor = (
  progress: MyRoadmapData["progress"] | undefined,
  credentialId: string
) => progress?.find((item) => item.credentialId === credentialId)?.status;

const profileValues = (value: string[] | string | null) =>
  Array.isArray(value) ? value : value ? [value] : [];

const RoadmapResult = ({
  data,
  onRetake,
  onUpdateProgress,
  updatingId,
  language,
  questionnaire,
}: RoadmapResultProps) => {
  if (!data?.roadmap) return null;

  const text =
    language === "ar"
      ? {
          eyebrow: "خطوتك المهنية القادمة تبدأ من هنا",
          title: "مسار مصمم حول أهدافك.",
          preparation: "الاستعداد",
          preparationDescription:
            "قوِّ هذه الأساسيات بالتوازي مع مسار شهاداتك المهنية.",
          notes: "ملاحظات",
          retake: "إعادة الاستبيان",
        }
      : {
          eyebrow: "Your next career breakthrough starts here",
          title: "A path built around your goals.",
          preparation: "Preparation",
          preparationDescription:
            "Strengthen these foundations alongside your certification path.",
          notes: "Notes",
          retake: "Retake questionnaire",
        };

  const specializationOptions = Object.values(
    questionnaire?.specialization.options_by_track ?? {}
  ).flat();
  const profileTags = [
    ...(data.roadmap.profile.career_stage
      ? [
          {
            value: data.roadmap.profile.career_stage,
            options: questionnaire?.career_stage.options ?? [],
          },
        ]
      : []),
    ...profileValues(data.roadmap.profile.career_track).map((value) => ({
      value,
      options: questionnaire?.career_track.options ?? [],
    })),
    ...profileValues(data.roadmap.profile.specialization).map((value) => ({
      value,
      options: specializationOptions,
    })),
  ];

  return (
  <div className="space-y-8">
    <div>
      <div className="mb-4 flex size-14 items-center justify-center rounded-2xl bg-secondary/10 text-secondary">
        <Sparkles />
      </div>
      <p className="text-xs font-bold uppercase tracking-[0.14em] text-secondary">
        {text.eyebrow}
      </p>
      <h2 className="mt-2 text-3xl font-bold text-primary sm:text-4xl">
        {text.title}
      </h2>
      <p className="mt-4 max-w-3xl text-sm leading-7 text-lightGray sm:text-base">
        <BilingualText value={ROADMAP_INTRO} language={language} />
      </p>

      <div className="mt-5 flex flex-wrap gap-2 text-xs font-bold text-primary">
        {profileTags.map(({ value, options }) => (
          <span
            key={value}
            className="rounded-full bg-[#edf0ee] px-3 py-1.5"
          >
            {localizedText(
              options.find((option) => option.id === value)?.label,
              language
            ) || value.replaceAll("_", " ")}
          </span>
        ))}
      </div>
    </div>

    {data.roadmap.preparation_step_0?.length > 0 && (
      <section>
        <div className="mb-4">
          <h3 className="text-xl font-bold text-primary">{text.preparation}</h3>
          <p className="mt-1 text-sm leading-6 text-lightGray">
            {text.preparationDescription}
          </p>
        </div>
        <ul className="grid gap-3 sm:grid-cols-2">
          {data.roadmap.preparation_step_0.map((item, index) => (
            <li
              key={`${item.en}-${index}`}
              className="flex items-start gap-3 rounded-2xl border border-black/5 bg-[#f8faf9] p-4 text-sm font-semibold leading-6 text-primary"
            >
              <span className="mt-0.5 flex size-7 shrink-0 items-center justify-center rounded-full bg-secondary/10 text-secondary">
                <Check size={15} strokeWidth={2.5} />
              </span>
              <BilingualText value={item} language={language} />
            </li>
          ))}
        </ul>
      </section>
    )}

    {sections.map(({ key, title }) => {
      const items = data.roadmap[key];
      if (!items?.length) return null;

      return (
        <section key={key}>
          <h3 className="mb-4 text-xl font-bold text-primary">
            <BilingualText value={title} language={language} />
          </h3>
          <div className="space-y-3">
            {items.map((item) => (
              <RoadmapCredentialCard
                key={item.credential_id || item.code}
                item={item}
                language={language}
                progressStatus={progressFor(data.progress, item.credential_id)}
                updating={updatingId === item.credential_id}
                onUpdateProgress={(status) =>
                  onUpdateProgress(item.credential_id, status)
                }
              />
            ))}
          </div>
        </section>
      );
    })}

    {Array.isArray(data.roadmap.notes) && data.roadmap.notes.length > 0 && (
      <section>
        <h3 className="mb-3 text-xl font-bold text-primary">{text.notes}</h3>
        <ul className="space-y-2">
          {data.roadmap.notes.map((note, index) => (
            <li
              key={index}
              className="rounded-xl bg-[#edf0ee] px-4 py-3 text-sm leading-6 text-primary"
            >
              <BilingualText
                value={note as LocalizedText | string}
                language={language}
              />
            </li>
          ))}
        </ul>
      </section>
    )}

    <button
      onClick={onRetake}
      className="flex items-center gap-2 rounded-full border border-black/10 px-5 py-3 text-sm font-bold text-primary transition hover:bg-[#f5f7f6]"
    >
      <RotateCcw size={16} /> {text.retake}
    </button>
  </div>
  );
};

export default RoadmapResult;
